import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Info } from "lucide-react";

interface Row {
  col: string;
  meaning: string;
  formula: string;
}

const ROWS: Row[] = [
  { col: "Assignment Status", meaning: "Whether the rental is assigned to a lender", formula: "Y from the disbursement month onwards; rentals due before disbursement remain N (Astrafin pre-assignment)" },
  { col: "Customer Pays", meaning: "Total receivable from customer (gross of GST)", formula: "Base + GST" },
  { col: "Security Deposit Movement", meaning: "Security Deposit inflow / refund", formula: "+ at Inception, − at SD Refund (full SD)" },
  { col: "PF In", meaning: "Processing Fee inflow (incl GST)", formula: "PF Base + PF GST" },
  { col: "Lender Disbursement", meaning: "Lender funds received against the assignment", formula: "On Inception if disbursed at/before commencement; otherwise on the disbursement-date row (Assign-Later)" },
  { col: "Vendor Payment", meaning: "Outflow to asset vendor (asset cost leg)", formula: "− (Base Asset Value + Asset GST)" },
  { col: "Output Tax Liability", meaning: "GST liability raised on the row", formula: "PF GST + Rental GST" },
  { col: "Opening ITC", meaning: "Input Tax Credit balance brought forward", formula: "Prior row's Closing ITC (seed = Asset GST + PF GST)" },
  { col: "ITC Utilised", meaning: "ITC consumed against Output Tax Liability", formula: "MIN(Output Tax Liability, Opening ITC)" },
  { col: "Closing ITC", meaning: "ITC carried forward to next period", formula: "Opening ITC − ITC Utilised" },
  { col: "Net GST Payable", meaning: "Cash GST remitted after ITC offset", formula: "Output Tax Liability − ITC Utilised" },
  { col: "TDS Withheld", meaning: "Tax Deducted at Source by customer", formula: "Rental: 2% / 10% of Base (locked at RS approval); PF: 10% of PF Base" },
  { col: "Cumulative TDS Withheld", meaning: "Running TDS withheld to date", formula: "Running Σ(TDS Withheld)" },
  { col: "Cashflow", meaning: "Net movement of the row", formula: "Customer Pays + SD + PF + Lender + Vendor − Net GST Payable − TDS" },
  { col: "Revenue to Astrafin", meaning: "Astrafin's share of the row", formula: "Assigned · Pre GST → OutputGST − GstPaid − TDS · Post GST → −GstPaid − TDS; Unassigned → CustomerPays − GstPaid − TDS; Lender Disbursement row → +Lender Disbursement" },
  { col: "Cumulative Astrafin Revenue", meaning: "Cumulative Astrafin revenue to date", formula: "Running Σ(Revenue to Astrafin)" },
  { col: "Lender Pay-through", meaning: "Amount routed/passed through to assignee lender", formula: "Assigned rental: Base (or Base + GST − TDS in pass-through); else 0" },
  { col: "Own Funds Deployed", meaning: "Astrafin's own capital used (proprietary funds)", formula: "−(Inception Cashflow to Astrafin) when negative; else 0" },
  { col: "Revenue Recognised (YTD)", meaning: "Cumulative Astrafin cashflow recognised up to today", formula: "Cumulative Astrafin Receipt at today's row" },
];

export const CashflowGlossarySheet = ({ trigger }: { trigger?: React.ReactNode }) => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-1.5 h-8" title="Column glossary">
            <Info className="h-3.5 w-3.5" />
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>Cashflow Forecast — Glossary</SheetTitle>
          <p className="text-xs text-muted-foreground">
            Definitions and formulas for every column in the cashflow waterfall.
          </p>
        </SheetHeader>
        <div className="rounded-md border">
          <Table className="text-xs">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead className="w-[180px]">Column</TableHead>
                <TableHead className="w-[200px]">Meaning</TableHead>
                <TableHead>Formula</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ROWS.map((r) => (
                <TableRow key={r.col}>
                  <TableCell className="font-medium align-top">{r.col}</TableCell>
                  <TableCell className="align-top text-muted-foreground">{r.meaning}</TableCell>
                  <TableCell className="align-top font-mono text-[11px]">{r.formula}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Rows with a grey background are past (settled) periods; default rows are future forecasts.
        </p>
      </SheetContent>
    </Sheet>
  );
};

export default CashflowGlossarySheet;
